import { useState, useEffect, useCallback } from 'react';
import { API, apiFetch } from '../api';
import { EXT_EVENT, EXT_REQUEST, EXT_READY_EVENT, requestExtensionStatus } from '../extensionEvents';

export default function ExtensionPanel({ user }) {
  const [extensionPresent, setExtensionPresent]   = useState(false);
  const [extStatus, setExtStatus]                 = useState(null); // { connected, username, steamId, lastSync, itemCount }
  const [linkingToken, setLinkingToken]           = useState(null);
  const [tokenExpiry, setTokenExpiry]             = useState(null);
  const [password, setPassword]                   = useState('');
  const [showPasswordBox, setShowPasswordBox]     = useState(false);
  const [syncing, setSyncing]                     = useState(false);
  const [syncProgress, setSyncProgress]           = useState('');
  const [error, setError]                         = useState('');
  const [success, setSuccess]                     = useState('');

  // ── Detect extension via custom event ──────────────────────
  useEffect(() => {
    const handler = () => setExtensionPresent(true);
    document.addEventListener(EXT_READY_EVENT, handler);
    // Probe: ask for status in case the ready event already fired before this mounted
    requestExtensionStatus();
    // Second probe after a short delay to handle slower content-script injection
    const t = setTimeout(requestExtensionStatus, 800);
    return () => { document.removeEventListener(EXT_READY_EVENT, handler); clearTimeout(t); };
  }, []);

  // ── Listen for messages from content script ────────────────
  useEffect(() => {
    function onMessage(event) {
      if (event.source !== window) return;
      const { type } = event.data || {};

      if (type === EXT_EVENT.STATUS) {
        setExtStatus(event.data);
        setExtensionPresent(true); // present regardless of whether linked
      }
      if (type === EXT_EVENT.LINK_RESULT) {
        if (event.data.success) {
          setLinkingToken(null);
          setShowPasswordBox(false);
          setPassword('');
          setSuccess('Extension connected successfully!');
          requestExtensionStatus();
        } else {
          setError(event.data.error || 'Linking failed');
        }
      }
      if (type === EXT_EVENT.SYNC_RESULT) {
        setSyncing(false);
        if (event.data.success) setSuccess(`Synced ${event.data.itemCount} items.`);
        else setError(event.data.error || 'Sync failed');
        requestExtensionStatus();
      }
      if (type === EXT_EVENT.SYNC_PROGRESS) setSyncProgress(event.data.message || '');
      if (type === EXT_EVENT.SYNC_COMPLETE) {
        setSyncing(false);
        setSyncProgress('');
        // A background sync (alarm or popup) also lands here, and the panel's
        // item count / last-sync are stale until we ask again.
        requestExtensionStatus();
      }
      if (type === EXT_EVENT.SYNC_ERROR) { setSyncing(false); setSyncProgress(''); setError(event.data.error || 'Sync failed'); }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Generate linking token ─────────────────────────────────
  //
  // The session is the proof, and the password box is the fallback. That order
  // matters now that Steam is the main sign-in: a Steam-created account has
  // password: null, so asking it for one first would leave every such account
  // permanently unable to connect the extension. Being signed in here already
  // means having proven the Steam account, which is a stronger claim than the
  // password ever was.
  const generateToken = useCallback(async () => {
    setError('');
    try {
      const res  = password
        ? await fetch(`${API}/api/ext-auth/linking-token`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ username: user, password }),
          })
        : await apiFetch('/api/ext-auth/linking-token', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    '{}',
          });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate token');

      setLinkingToken(data.linkingToken);
      setTokenExpiry(data.expiresAt);
      setPassword('');
      setShowPasswordBox(false);

      // Send token to extension via content script bridge
      window.postMessage({ type: EXT_REQUEST.LINK_TOKEN, token: data.linkingToken }, window.location.origin);
      setSuccess('Token generated and sent to extension. Check the extension popup.');
    } catch (err) {
      setError(err.message);
      // The session path failed, so fall back to asking for a password. An
      // account that has none simply cannot get past this, which is correct:
      // its only credential is the session that just turned out to be dead.
      if (!password) setShowPasswordBox(true);
    }
  }, [password, user]);

  // ── Trigger sync via extension ─────────────────────────────
  const triggerSync = () => {
    setError('');
    setSyncing(true);
    setSyncProgress('Requesting sync…');
    window.postMessage({ type: EXT_REQUEST.SYNC }, window.location.origin);
  };

  const formatTime = iso => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isConnected = extStatus?.connected;

  return (
    <div style={{
      background: 'var(--panel, #0b1220)',
      border: '1px solid var(--border, rgba(255,255,255,0.08))',
      borderRadius: 10,
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text, #e5e7eb)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Browser Extension
        </span>
        <span style={{
          fontSize: 14.5, fontWeight: 600, padding: '2px 9px', borderRadius: 100,
          background: isConnected ? 'rgba(74,222,128,0.12)' : extensionPresent ? 'rgba(251,191,36,0.12)' : 'rgba(248,113,113,0.12)',
          color: isConnected ? '#4ade80' : extensionPresent ? '#fbbf24' : '#f87171',
        }}>
          {isConnected ? 'Connected' : extensionPresent ? 'Extension detected' : 'Not detected'}
        </span>
      </div>

      {/* Status grid */}
      {isConnected && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px',
          background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '10px 14px',
          fontSize: 15,
        }}>
          <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Account</span>
          <span style={{ fontWeight: 600 }}>{extStatus.username}</span>
          <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Steam ID</span>
          <span style={{ fontFamily: 'monospace', fontSize: 14.5 }}>{extStatus.steamId || '—'}</span>
          <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Items</span>
          <span>{extStatus.itemCount != null ? `${extStatus.itemCount} items` : '—'}</span>
          <span style={{ color: 'var(--text-muted, #9ca3af)' }}>Last sync</span>
          <span>{formatTime(extStatus.lastSync)}</span>
        </div>
      )}

      {/* Sync progress */}
      {syncing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: '60%', borderRadius: 2,
              background: 'var(--indigo, #818cf8)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          </div>
          <span style={{ fontSize: 15, color: 'var(--text-muted, #9ca3af)' }}>{syncProgress}</span>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {isConnected ? (
          <button
            onClick={triggerSync}
            disabled={syncing}
            style={{
              padding: '7px 14px', borderRadius: 7, fontSize: 15, fontWeight: 600,
              background: 'var(--indigo, #818cf8)', color: '#fff', border: 'none', cursor: 'pointer',
              opacity: syncing ? 0.5 : 1,
            }}
          >
            {syncing ? 'Syncing…' : 'Sync Inventory'}
          </button>
        ) : extensionPresent ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {!showPasswordBox ? (
              <button
                /* Straight to a token: being signed in here is the proof. The
                   password box below is only reached if the server rejects
                   that, which now means the session itself has expired. */
                onClick={generateToken}
                style={{
                  padding: '7px 14px', borderRadius: 7, fontSize: 15, fontWeight: 600,
                  background: 'var(--indigo, #818cf8)', color: '#fff', border: 'none', cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                Connect Extension
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && generateToken()}
                  placeholder="Enter your password to link…"
                  style={{
                    flex: 1, minWidth: 200, padding: '7px 10px', borderRadius: 7, fontSize: 15,
                    background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#e5e7eb', outline: 'none',
                  }}
                />
                <button onClick={generateToken} style={{
                  padding: '7px 14px', borderRadius: 7, fontSize: 15, fontWeight: 600,
                  background: 'var(--indigo, #818cf8)', color: '#fff', border: 'none', cursor: 'pointer',
                }}>
                  Generate Token
                </button>
                <button onClick={() => { setShowPasswordBox(false); setPassword(''); }} style={{
                  padding: '7px 10px', borderRadius: 7, fontSize: 15,
                  background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
                }}>
                  Cancel
                </button>
              </div>
            )}
            {linkingToken && (
              <div style={{ fontSize: 14.5, color: '#9ca3af' }}>
                Token sent to extension. Expires: {tokenExpiry ? new Date(tokenExpiry).toLocaleTimeString() : '—'}
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 15, color: 'var(--text-muted, #9ca3af)', margin: 0 }}>
            Install the CS2 Profit Tracker browser extension to enable automatic inventory sync.
            See <code>extension/README.md</code> for installation instructions.
          </p>
        )}
      </div>

      {/* Messages */}
      {error   && <p style={{ fontSize: 15, color: '#f87171', margin: 0 }}>{error}</p>}
      {success && <p style={{ fontSize: 15, color: '#4ade80', margin: 0 }}>{success}</p>}
    </div>
  );
}
